import axios from 'axios';
import { getIdToken } from 'firebase/auth';
import { auth } from './firebase';
import { useTenantStore } from '../state/tenant';
import { logout } from '../components/AuthBoundary';

const configuredBaseUrl = (import.meta.env.VITE_API_BASE_URL || '').trim();
const SESSION_TOKEN_KEY = 'kyra_session_token';

const api = axios.create({
  baseURL: configuredBaseUrl || undefined,
});

function getUserSessionTokenKey() {
  const uid = auth.currentUser?.uid;
  return uid ? `${SESSION_TOKEN_KEY}:${uid}` : null;
}

export function setApiSessionToken(token: string | null | undefined) {
  const userSessionTokenKey = getUserSessionTokenKey();
  if (token) {
    if (userSessionTokenKey) {
      localStorage.setItem(userSessionTokenKey, token);
    }
  } else {
    if (userSessionTokenKey) {
      localStorage.removeItem(userSessionTokenKey);
    }
    localStorage.removeItem(SESSION_TOKEN_KEY);
  }
}

export function getApiSessionToken() {
  const userSessionTokenKey = getUserSessionTokenKey();
  return userSessionTokenKey ? localStorage.getItem(userSessionTokenKey) : null;
}

api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    // Do not force refresh on every request; Firebase SDK refreshes automatically when needed.
    const token = await getIdToken(user);
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  const { activeOrganizationId, claims } = useTenantStore.getState();
  const orgId = activeOrganizationId ?? claims?.org_id;
  if (orgId) {
    config.headers = config.headers ?? {};
    config.headers['x-org-id'] = orgId;
  }
  const sessionToken = getApiSessionToken();
  if (sessionToken) {
    config.headers = config.headers ?? {};
    config.headers['x-session-token'] = sessionToken;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status === 401) {
      setApiSessionToken(null);
      await logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

export { api };
